/**
 * UnderstandStage — the "make it make sense" step.
 *
 * Deterministic plain-language breakdown pulled from the backend
 * (`GET /api/wallet/decisions/{id}/understand`).
 *
 * Consumers:
 *   • Read four key-facts cards (what / cost / earn / horizon).
 *   • Read three "what could happen" scenarios (good / expected / bad).
 *   • Tick five "I understand …" attestations.
 *   • Optionally save adviser questions to their record.
 *
 * When every attestation is ticked, the "advance to Check" action
 * unlocks and posts to `/understand/attest` which advances the decision.
 */
import { useEffect, useState } from "react";
import {
  Sparkles, TrendingUp, TrendingDown, Wallet, Clock, HelpCircle,
  CheckCircle2, ArrowRight, Save, MessageSquare, Info,
} from "lucide-react";
import { NAVY, GOLD } from "@/components/wallet/WalletShell";
import { fetchUnderstand, attestUnderstand, zar } from "@/lib/wallet-decisions";
import NaviFace from "@/components/wallet/NaviFace";

const KEY_FACT_ICONS = {
  what_you_are_buying:  Sparkles,
  what_it_costs:        Wallet,
  what_you_might_earn:  TrendingUp,
  time_horizon:         Clock,
};

export default function UnderstandStage({ decision, deviceId, onAdvance }) {
  const done = decision.status !== "UNDERSTAND_PENDING";
  const [data, setData] = useState(null);
  const [attested, setAttested] = useState(() => new Set(decision.understand_attest?.attested_ids || []));
  const [savedQs, setSavedQs] = useState(() => new Set(decision.understand_attest?.saved_questions || []));
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let c = false;
    (async () => {
      try {
        const d = await fetchUnderstand(decision.decision_id, deviceId);
        if (!c) setData(d);
      } catch (e) { if (!c) setErr(e?.response?.data?.detail || "Could not load."); }
    })();
    return () => { c = true; };
  }, [decision.decision_id, deviceId]);

  function toggleAttest(id) {
    if (done) return;
    setAttested(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }
  function toggleQuestion(q) {
    setSavedQs(prev => {
      const next = new Set(prev);
      if (next.has(q)) next.delete(q); else next.add(q);
      return next;
    });
  }

  if (!data && !err) {
    return <div className="rounded-2xl border border-slate-100 bg-white p-8 text-center animate-pulse text-slate-400" data-testid="understand-loading">Loading Understand…</div>;
  }
  if (err) return <div className="text-[12px] text-red-700" data-testid="understand-error">{err}</div>;

  const b = data.breakdown;
  const total = b.attestations.length;
  const nTicked = b.attestations.filter(a => attested.has(a.id)).length;
  const allTicked = nTicked === total;

  async function submit() {
    if (!allTicked || busy || done) return;
    setBusy(true); setErr(null);
    try {
      const updated = await attestUnderstand(decision.decision_id, deviceId, [...attested], [...savedQs]);
      onAdvance(updated);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Could not save.");
    } finally { setBusy(false); }
  }

  return (
    <section className="space-y-4" data-testid="understand-stage">
      {/* NAVI intro */}
      <div className="rounded-2xl border border-slate-100 bg-white p-4 flex items-start gap-3">
        <NaviFace size="md" />
        <div className="min-w-0">
          <div className="text-[13px] font-semibold" style={{ color: NAVY }}>Let's make sure this proposal actually makes sense to you.</div>
          <div className="text-[12px] text-slate-600 mt-1 leading-relaxed">
            I've distilled the proposal down to four key facts and three possible outcomes. Read them carefully — then tick each "I understand" statement below when you honestly do.
          </div>
        </div>
      </div>

      {/* Key facts */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="understand-key-facts">
        {b.key_facts.map(f => {
          const Icon = KEY_FACT_ICONS[f.id] || Info;
          return (
            <div key={f.id} className="rounded-2xl border border-slate-100 bg-white p-4" data-testid={`understand-key-${f.id}`}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: `${GOLD}22` }}>
                  <Icon className="w-4 h-4" style={{ color: NAVY }} />
                </div>
                <div className="text-[10.5px] uppercase tracking-widest font-semibold text-slate-500">{f.label}</div>
              </div>
              <div className="mt-2 font-display text-[15px] font-semibold" style={{ color: NAVY }}>{f.value}</div>
              <div className="text-[12px] text-slate-600 mt-1 leading-relaxed">{f.detail}</div>
            </div>
          );
        })}
      </div>

      {/* Scenarios */}
      <div className="rounded-2xl border border-slate-100 bg-white p-4 lg:p-5" data-testid="understand-scenarios">
        <div className="flex items-center gap-2 mb-3">
          <div className="text-[10.5px] uppercase tracking-[0.24em] font-semibold" style={{ color: GOLD }}>
            What could happen · plain scenarios
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {b.scenarios.map(s => (
            <ScenarioCard key={s.id} s={s} />
          ))}
        </div>
        <div className="mt-3 text-[11px] text-slate-500 leading-relaxed flex items-start gap-1.5">
          <Info className="w-3 h-3 mt-0.5 flex-shrink-0" />
          These are projections based on the return the proposal itself sets out. Real markets don't move in a straight line — the "bad years" column is why long-term investing is uncomfortable.
        </div>
      </div>

      {/* Attestations */}
      <div className="rounded-2xl border border-slate-100 bg-white p-4 lg:p-5" data-testid="understand-attestations">
        <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
          <div>
            <div className="font-display text-[14.5px] font-semibold" style={{ color: NAVY }}>Confirm you understand</div>
            <div className="text-[11.5px] text-slate-500">Tick every statement you honestly agree with.</div>
          </div>
          <div className="text-[11px] font-mono font-semibold" style={{ color: allTicked ? "#059669" : NAVY }}>
            {nTicked} / {total}
          </div>
        </div>
        <ul className="space-y-2">
          {b.attestations.map(a => {
            const checked = attested.has(a.id);
            return (
              <li key={a.id} data-testid={`understand-attest-${a.id}`}>
                <button
                  onClick={() => toggleAttest(a.id)}
                  disabled={done}
                  className="w-full text-left flex items-start gap-3 rounded-xl p-3 border transition"
                  style={{
                    borderColor: checked ? "#A7F3D0" : "#E2E8F0",
                    background:  checked ? "#F0FDF4" : "white",
                    cursor:      done ? "default" : "pointer",
                  }}
                >
                  <span
                    className="w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 mt-0.5 border-2"
                    style={{
                      background:  checked ? "#059669" : "white",
                      borderColor: checked ? "#059669" : "#CBD5E1",
                    }}
                  >
                    {checked && <CheckCircle2 className="w-3 h-3 text-white" />}
                  </span>
                  <span className="text-[12.5px] leading-relaxed" style={{ color: NAVY }}>{a.text}</span>
                </button>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Suggested questions */}
      <div className="rounded-2xl border border-slate-100 bg-white p-4 lg:p-5" data-testid="understand-questions">
        <div className="flex items-center gap-2 mb-2">
          <MessageSquare className="w-4 h-4" style={{ color: GOLD }} />
          <div className="font-display text-[14px] font-semibold" style={{ color: NAVY }}>Questions worth asking your adviser</div>
        </div>
        <div className="text-[11.5px] text-slate-500 mb-3">Save the ones you want in your record — you can revisit them later.</div>
        <ul className="space-y-2">
          {b.suggested_questions.map(q => {
            const saved = savedQs.has(q);
            return (
              <li key={q} className="flex items-start gap-2" data-testid="understand-question">
                <button
                  onClick={() => toggleQuestion(q)}
                  className="w-6 h-6 rounded-full flex items-center justify-center flex-shrink-0 border transition"
                  style={{
                    background:  saved ? GOLD : "white",
                    borderColor: saved ? GOLD : "#CBD5E1",
                  }}
                  aria-pressed={saved}
                  title={saved ? "Saved" : "Save"}
                >
                  <Save className="w-3 h-3" style={{ color: saved ? "white" : "#94A3B8" }} />
                </button>
                <span className="text-[12.5px] leading-relaxed" style={{ color: NAVY }}>{q}</span>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Action */}
      {done ? (
        <div className="rounded-2xl border p-4 flex items-center gap-3" style={{ background: "#ECFDF5", borderColor: "#A7F3D0" }} data-testid="understand-completed-banner">
          <CheckCircle2 className="w-5 h-5" style={{ color: "#059669" }} />
          <div className="text-[13px]" style={{ color: "#065F46" }}>
            <div className="font-semibold">Understand step complete</div>
            <div className="text-[12px] mt-0.5">All attestations recorded to the evidence trail.</div>
          </div>
        </div>
      ) : (
        <div className="rounded-2xl border border-slate-100 bg-white p-4 flex items-center justify-between gap-3 flex-wrap" data-testid="understand-action-row">
          <div className="text-[12px] text-slate-600 leading-relaxed">
            When every "I understand" is ticked, this decision advances to <span className="font-semibold" style={{ color: NAVY }}>Check</span>.
          </div>
          <button
            onClick={submit}
            disabled={!allTicked || busy}
            data-testid="understand-advance-btn"
            className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-[13px] font-semibold text-white disabled:opacity-40"
            style={{ background: NAVY }}
          >
            {busy ? "Recording…" : "I understand — advance to Check"}
            <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
      )}
      {err && <div className="text-[12px] text-red-700" data-testid="understand-error">{err}</div>}
    </section>
  );
}

function ScenarioCard({ s }) {
  const tone = s.id === "best" ? "#059669" : s.id === "bad" ? "#B91C1C" : NAVY;
  const bg   = s.id === "best" ? "#ECFDF5" : s.id === "bad" ? "#FEF2F2" : "#F8FAFC";
  const Icon = s.id === "bad" ? TrendingDown : s.id === "best" ? TrendingUp : Sparkles;
  return (
    <div className="rounded-xl p-3 border" style={{ background: bg, borderColor: "transparent" }} data-testid={`scenario-${s.id}`}>
      <div className="flex items-center gap-1.5">
        <Icon className="w-3.5 h-3.5" style={{ color: tone }} />
        <div className="text-[10.5px] uppercase tracking-widest font-semibold" style={{ color: tone }}>{s.label}</div>
      </div>
      <div className="mt-2 font-mono font-semibold text-[15px]" style={{ color: NAVY }}>{zar(s.final_value_zar)}</div>
      <div className="text-[10.5px] text-slate-500 mt-0.5">at {s.return_pct}% p.a.</div>
      <div className="text-[11px] text-slate-600 mt-2 leading-snug">{s.note}</div>
    </div>
  );
}
